// components/chat-surface/chat-message-list.js
// ═══════════════════════════════════════════════════════════
// Renders a list of messages with three reading modes:
//   - default: current styling
//   - kindle: warm, spacious, optimized for long reads
//   - compact: tighter, for quick back-and-forth
//
// Includes: streaming indicator, collapse long messages,
// code block copy button, message toolbars.
// ═══════════════════════════════════════════════════════════

import { useState, memo } from 'react';
import MessageContent from '../message-content';
import { AIToolbar, UserToolbar, StreamingDots, ChatToolbarStyles } from './chat-message-toolbar';

// ── Reading mode config ──
const READING_MODES = {
  default: {
    maxWidth: 760,
    fontSize: 'var(--msg-body-size, 14px)',
    lineHeight: 1.7,
    paragraphGap: 0,
  },
  kindle: {
    maxWidth: 640,
    fontSize: '15px',
    lineHeight: 1.85,
    paragraphGap: 6,
  },
  compact: {
    maxWidth: 820,
    fontSize: '13px',
    lineHeight: 1.5,
    paragraphGap: 0,
  },
};

// ── Collapse threshold ──
const COLLAPSE_THRESHOLD = 1500; // characters

// ── Single message component ──
const ChatMessage = memo(function ChatMessage({
  m, i, isLastUser, lastUserMsgRef, userRecord, readingMode,
  onPin, onRegenerate, onEdit, onDelete,
}) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isUser = m.role === 'user';
  const isStreaming = m.streaming;
  const mode = READING_MODES[readingMode] || READING_MODES.default;

  // Collapse long messages
  const content = typeof m.content === 'string' ? m.content : '';
  const isLong = content.length > COLLAPSE_THRESHOLD && !isStreaming;
  const shouldCollapse = isLong && !expanded;

  return (
    <div
      ref={isLastUser ? lastUserMsgRef : null}
      data-msg-role={m.role}
      data-msg-id={m.id || ''}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        marginBottom: 18,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        maxWidth: mode.maxWidth,
        width: '100%',
        alignSelf: 'center',
      }}
    >
      {/* Sender label */}
      <div style={{
        fontSize: 9,
        color: isUser ? '#888' : '#c8a96e',
        marginBottom: 4,
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}>
        {isUser ? (userRecord?.first_name || 'You') : (userRecord?.ai_name || 'Shift')}
      </div>

      {/* Message bubble */}
      <div style={{
        maxWidth: '88%',
        fontSize: mode.fontSize,
        lineHeight: mode.lineHeight,
        color: 'var(--text-1)',
        fontFamily: 'var(--font-serif)',
        WebkitFontSmoothing: 'antialiased',
        background: isUser ? '#111118' : 'none',
        border: isUser ? '1px solid #252018' : 'none',
        borderRadius: isUser ? '12px 12px 2px 12px' : 0,
        padding: isUser ? '10px 14px' : '0',
        whiteSpace: 'pre-wrap',
        position: 'relative',
        ...(shouldCollapse ? {
          maxHeight: 200,
          overflow: 'hidden',
          maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
        } : {}),
      }}>
        {/* Streaming states */}
        {!isUser && isStreaming && !m.content ? (
          <StreamingDots />
        ) : !isUser && isStreaming && m.content ? (
          <>
            <MessageContent m={m} i={i} />
            <StreamingDots />
          </>
        ) : (
          <MessageContent m={m} i={i} />
        )}
      </div>

      {/* Show more button for collapsed messages */}
      {shouldCollapse && (
        <button
          onClick={() => setExpanded(true)}
          style={{
            fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif',
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px 0', marginTop: 2,
          }}
        >
          Show more ↓
        </button>
      )}
      {isLong && expanded && (
        <button
          onClick={() => setExpanded(false)}
          style={{
            fontSize: 9, color: '#555', fontFamily: 'Georgia, serif',
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px 0', marginTop: 2,
          }}
        >
          Show less ↑
        </button>
      )}

      {/* Toolbars — hover-reveal */}
      {(!isStreaming || isUser) && (
        <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
          {isUser ? (
            <UserToolbar message={m} onEdit={onEdit} onDelete={onDelete} />
          ) : (
            <AIToolbar
              message={m}
              streaming={isStreaming}
              onPin={onPin}
              onRegenerate={onRegenerate}
            />
          )}
        </div>
      )}
    </div>
  );
});

// ── Message list wrapper ──
export default function ChatMessageList({
  messages,
  lastUserMsgRef,
  userRecord,
  readingMode = 'default',
  onPin,
  onRegenerate,
  onEdit,
  onDelete,
  // Render slots for extra content between messages
  renderAfterMessage,
}) {
  const lastUserIndex = messages.reduce((acc, m, i) => m.role === 'user' ? i : acc, -1);

  return (
    <>
      <ChatToolbarStyles />
      <div style={{
        padding: '24px 24px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100%',
      }}>
        {messages.map((m, i) => (
          <div key={m.id || i} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ChatMessage
              m={m}
              i={i}
              isLastUser={i === lastUserIndex}
              lastUserMsgRef={lastUserMsgRef}
              userRecord={userRecord}
              readingMode={readingMode}
              onPin={onPin}
              onRegenerate={onRegenerate}
              onEdit={onEdit}
              onDelete={onDelete}
            />
            {renderAfterMessage?.(m, i)}
          </div>
        ))}
      </div>
    </>
  );
}
